import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTimeout(() => el.classList.add('visible'), delay); obs.unobserve(el); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

const guides = [
  {
    icon: 'ri-home-search-line',
    label: 'For Buyers',
    title: 'Complete Buyer Guide',
    desc: 'Everything you need to know about the home buying process in Southeast Michigan — from pre-approval to closing day. Free download.',
    cta: 'Download Buyer Guide',
    href: 'https://movewithkendrick.com/buyer-guide',
    img: 'https://public.readdy.ai/ai/img_res/c9a9222826e1d39cd60b225648a0a0bc.jpg',
    badge: 'Free Download',
  },
  {
    icon: 'ri-price-tag-3-line',
    label: 'For Sellers',
    title: 'Complete Seller Guide',
    desc: 'Learn how to price, prepare, and market your home for maximum value. Step-by-step guidance from a realtor who knows what buyers really look for. Free download.',
    cta: 'Download Seller Guide',
    href: 'https://movewithkendrick.com/seller-guide',
    img: 'https://readdy.ai/api/search-image?query=professional%20real%20estate%20seller%20guide%20booklet%20on%20wooden%20table%20with%20small%20house%20model%20and%20sold%20sign%20clean%20minimal%20home%20office%20warm%20lighting&width=700&height=500&seq=guide-seller-v2&orientation=landscape',
    badge: 'Free Download',
  },
  {
    icon: 'ri-file-list-3-line',
    label: 'Coming Soon',
    title: 'First-Time Buyer Checklist',
    desc: 'A step-by-step checklist built specifically for first-time buyers in Michigan and Ohio — what to do, when to do it, and what to watch out for.',
    cta: 'Notify Me',
    href: '/contact',
    img: 'https://readdy.ai/api/search-image?query=clean%20checklist%20notebook%20with%20pencil%20on%20white%20desk%20minimalist%20home%20office%20modern%20design%20warm%20natural%20lighting%20productivity%20planning&width=700&height=500&seq=guide-checklist-v2&orientation=landscape',
    badge: 'Coming Soon',
  },
  {
    icon: 'ri-building-4-line',
    label: 'Coming Soon',
    title: 'Estate & Probate Selling Guide',
    desc: 'A gentle, practical guide to selling an inherited or estate property. Understand the process, the paperwork, and how to make a difficult time a little easier.',
    cta: 'Notify Me',
    href: '/contact',
    img: 'https://readdy.ai/api/search-image?query=family%20home%20exterior%20classic%20brick%20house%20with%20large%20windows%20lush%20garden%20peaceful%20neighborhood%20warm%20afternoon%20light%20estate%20property%20suburban%20michigan&width=700&height=500&seq=guide-estate-v2&orientation=landscape',
    badge: 'Coming Soon',
  },
];

export default function ResourcesPage() {
  const headingRef = useReveal(0);
  const card0 = useReveal(0); const card1 = useReveal(120);
  const card2 = useReveal(0); const card3 = useReveal(120);
  const cardRefs = [card0, card1, card2, card3];
  const ctaRef = useReveal(0);

  return (
    <div className="min-h-screen bg-[#FAF9F7] page-enter">

      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-14 sm:pb-20 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 animate-fade-in delay-100 font-medium">Free Resources</p>
          <h1 className="font-cormorant text-4xl sm:text-6xl lg:text-7xl font-light text-[#111111] mb-6 animate-fade-in-up delay-200 leading-tight">
            Tools to Help You<br /><em className="not-italic font-semibold">Move Confidently</em>
          </h1>
          <p className="text-[#666] leading-relaxed animate-fade-in-up delay-300 max-w-xl mx-auto text-sm sm:text-base">
            Free guides, checklists, and tools designed to help buyers and sellers in Southeast Michigan and nearby Ohio make smarter, less stressful moves.
          </p>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-14 sm:py-20 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div ref={headingRef} className="reveal text-center mb-10 sm:mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Available Guides</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111]">Buyer &amp; Seller Guides</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            {guides.map((guide, i) => (
              <div key={i} ref={cardRefs[i]} className="reveal scale bg-white rounded-lg overflow-hidden hover-lift border border-gray-100 group">
                <div className="relative h-44 sm:h-52 overflow-hidden">
                  <img src={guide.img} alt={guide.title} className="w-full h-full object-cover object-top transition-transform duration-600 group-hover:scale-105" />
                  <div className="absolute top-4 left-4">
                    <span className={`text-xs font-semibold px-3 py-1.5 rounded-full uppercase tracking-wider ${guide.badge === 'Free Download' ? 'bg-[#C8923A] text-white' : 'bg-[#1C3A6B] text-white'}`}>
                      {guide.badge}
                    </span>
                  </div>
                </div>
                <div className="p-6 sm:p-8">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#C8923A] mb-3 font-medium">{guide.label}</p>
                  <h3 className="font-cormorant text-xl sm:text-2xl font-semibold text-[#111111] mb-3">{guide.title}</h3>
                  <div className="w-8 h-0.5 bg-[#C8923A] mb-4"></div>
                  <p className="text-sm text-[#666] leading-relaxed mb-6">{guide.desc}</p>
                  {guide.badge === 'Free Download' ? (
                    <a href={guide.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 bg-[#1C3A6B] text-white text-sm font-semibold rounded-md hover:bg-[#152E55] transition-all whitespace-nowrap">
                      <i className="ri-download-line"></i>
                      {guide.cta}
                    </a>
                  ) : (
                    <Link to={guide.href} className="inline-flex items-center gap-2 text-sm font-semibold text-[#1C3A6B] hover:text-[#C8923A] transition-colors whitespace-nowrap group/link">
                      {guide.cta}
                      <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 sm:py-20 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <div ref={ctaRef} className="reveal scale bg-[#1C3A6B] rounded-lg p-8 sm:p-12 text-center text-white">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Personalized Guidance</p>
            <h2 className="font-cormorant text-3xl sm:text-4xl font-light mb-4 leading-tight">Not Sure Where to Start?</h2>
            <p className="text-white/70 mb-6 sm:mb-8 max-w-xl mx-auto text-sm sm:text-base">A free 15-minute call can answer your questions and help you figure out your next best step — no pressure, no commitment.</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <a href="tel:+15176629155" className="px-7 sm:px-8 py-3.5 sm:py-4 bg-[#C8923A] text-white font-semibold text-sm rounded-md hover:bg-[#B07B2A] transition-all whitespace-nowrap inline-flex items-center justify-center gap-2">
                <i className="ri-phone-line"></i> Call Kendrick
              </a>
              <Link to="/contact" className="px-7 sm:px-8 py-3.5 sm:py-4 border border-white/30 text-white font-semibold text-sm rounded-md hover:bg-white/10 transition-all whitespace-nowrap">
                Send a Message
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
